import API from 'core/api'
import { STORE_CURATION, STORE_CURATION_LOADING } from 'core/store/curation'
import { STORE_MODULE } from 'core/store/global'
import { useCallback, useEffect, useState } from 'react'
import { useAsync } from 'react-async'
import { useRecoilValue, useSetRecoilState } from 'recoil'

/**
 * 전체 큐레이션 데이터를 가져오는 hooks
 */
export default function useCurationDetail(curationId?: string) {
  const setCurationLoading = useSetRecoilState(STORE_CURATION_LOADING)
  const setCurationList = useSetRecoilState(STORE_CURATION)
  const moduleName = useRecoilValue(STORE_MODULE)
  const [nextKey, setNextKey] = useState<string>('')

  const { data, isLoading } = useAsync({
    promiseFn: API.getCuration,
    nextKey,
    watch: nextKey,
    moduleName,
    curationId,
  })

  const { data: curationDetailData, isLoading: isCurationDetailLoading } = useAsync({
    promiseFn: API.getCurationDetail,
    curationId: curationId,
    nextKey,
    limit: 10,
    moduleName,
  })

  /**
   * @name LifeCycleHook
   * Curation 상세 정보를 불러오는 API 호출
   **/

  const getCurationDetail = useCallback(
    async (curationList?: CurationItem[], nextKey?: string) => {
      if (curationId && curationDetailData?.curation?.isPublic) {
        const res = [
          {
            curationDetailList: curationDetailData?.items || [],
            curationName: curationDetailData?.curation?.curationName,
          },
        ] as StoreCurationItems[]
        setCurationList([...res])
        if (nextKey) {
          setNextKey(nextKey)
        }
        return
      }
      if (!curationList) return
      const res = await Promise.all(
        curationList.map(async (curationItem) => {
          const { curationId } = curationItem
          const res = await API.getCurationDetail({
            curationId,
            nextKey: 'init',
            limit: 10,
            moduleName,
          })

          return {
            ...curationItem,
            curationDetailList: res?.items || [],
          } as StoreCurationItems
        }),
      )
      setCurationList((prev) => [
        ...prev,
        ...res?.filter(({ curationDetailList }) => curationDetailList.length > 0),
      ])
      if (nextKey) {
        setNextKey(nextKey)
      }
    },
    [
      curationDetailData?.curation?.curationName,
      curationDetailData?.curation?.isPublic,
      curationDetailData?.items,
      curationId,
      moduleName,
      setCurationList,
    ],
  )
  /**
   * @description promiseFn: API.getCurationDetail 에서 사용되는 데이터만 이용 curationId (단일 큐레이션 모듈화용)
   */
  useEffect(() => {
    if (curationId) {
      getCurationDetail()
      return
    }
  }, [curationId, getCurationDetail])
  /**
   * @description promiseFn: API.getCuration 에서 사용되는 data 변수 (전체 큐레이션 데이터 로딩용)
   */
  useEffect(() => {
    if (data) {
      getCurationDetail(
        data.items.filter(({ liveCount, vodCount }) => liveCount + vodCount > 0),
        data.nextKey,
      )
    }
  }, [data, getCurationDetail])

  useEffect(() => {
    // recoil에 Curation API loading 상태 업데이트
    setCurationLoading(isLoading)
    curationId && setCurationLoading(isCurationDetailLoading)
  }, [curationId, isCurationDetailLoading, isLoading, setCurationLoading])

  useEffect(() => {
    setCurationList([])
  }, [setCurationList])
}
